import { GlobalStyle } from '@designsforhealth/dfh-react-components'
import { Redirect, RouteComponentProps, Router, useLocation } from '@gatsbyjs/reach-router'
import React, { Fragment, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'

import { GetCustomerAccountHistoryDocument } from '../../../graphql/camel'
import { SanityAccountDashboardPageFragment } from '../../../graphql/gatsby'
import useCamelQuery from '../../../hooks/useCamelQuery'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import useSiteMetadata from '../../../hooks/useSiteMetadata'
import useSiteSettings from '../../../hooks/useSiteSettings'
import useUserCategory from '../../../hooks/useUserCategory'
import Head from '../../../layouts/Head'
import { isInvalidStateError, isUserBlockedError, useAuth } from '../../../lib/auth'
import { getRouterBasePath, useDispensaryContext } from '../../../lib/dispensaries'
import { usePageUrl } from '../../../lib/pages'
import useRewardsOrders from '../../../lib/rewards/hooks/useRewardsOrders'
import { langCodes } from '../../../utils/siteSettings'
import { EmptyObject } from '../../../utils/types'
import { LoginError } from '../../auth/LoginError'
import Layout from '../../global/Layout'
import Spinner from '../../Spinner'
import AccountHistoryContainer from './account_history/AccountHistoryContainer'
import { AccountRouteNotFound } from './AccountRouteNotFound'
import AddressBookContainer from './address_book/AddressBookContainer'
import EditAddressContainer from './address_book/EditAddressContainer'
import NewAddressContainer from './address_book/NewAddressContainer'
import AgreementsContainer from './agreements/AgreementsContainer'
import AutoShipsContainer from './auto_ships/AutoShipsContainer'
import Container from './Container'
import { CreditCardsContainer } from './credit_cards/CreditCardsContainer'
import Dashboard from './dashboard/Dashboard'
import PatientDispensaryContact from './dispensary/contact/PatientDispensaryContact'
import PractitionerDispensaryContact from './dispensary/contact/PractitionerDispensaryContact'
import CustomizeFeaturedProducts from './dispensary/customize/featured-products/CustomizeFeaturedProducts'
import CustomizeHomePage from './dispensary/customize/home-page/CustomizeHomePage'
import CustomizeLogoAndBio from './dispensary/customize/logo-bio/CustomizeLogoAndBio'
import DispensaryDashboard from './dispensary/dashboard/DispensaryDashboard'
import DispensaryPromosPage from './dispensary/promos/DispensaryPromosPage'
import Home, { HomeProps } from './Home'
import CustomerInvoicesPage from './invoices/CustomerInvoicesPage'
import CustomerInvoiceContainer from './invoices/invoice/CustomerInvoiceContainer'
import CustomerOrdersPage from './orders/CustomerOrdersPage'
import CustomerOrderPage from './orders/order/CustomerOrderPage'
import RewardsOrderPage from './orders/order/RewardsOrderPage'
import ReorderItemsPage from './orders/ReorderItemsPage'
import RewardsOrdersPage from './orders/RewardsOrdersPage'
import Returns from './Returns'
import { RewardsDetailsPayouts } from './rewards/details-payouts/RewardsDetailsPayouts'
import AccountDocumentsContainer from './settings/AccountDocumentsContainer'
import DispensingOptionsContainer from './settings/DispensingOptionsContainer'
import EftAndTaxContainer from './settings/EftAndTaxContainer'
import PasswordSettings from './settings/PasswordSettings'
import RewardsProgramContainer from './settings/RewardsProgramContainer'
import CustomerShipmentsContainer from './shipments/CustomerShipmentsContainer'
import RewardsShipmentsContainer from './shipments/RewardsShipmentsContainer'

export type AccountProps = {
  className?: string
  sanityAccountDashboardPage: SanityAccountDashboardPageFragment['sanityAccountDashboardPage']
}

type PrivateRouteProps<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TComponent extends React.ComponentType<any>,
  TParamProps extends Exclude<keyof React.ComponentProps<TComponent>, 'children'> = never,
> = RouteComponentProps<EmptyObject> &
  Omit<React.ComponentProps<TComponent>, TParamProps> & {
    component: TComponent
    // props supplied by reach-router dynamic segments
    paramProps?: ReadonlyArray<TParamProps>
  }

const getPrivateRouteComponent = (homeProps: HomeProps) => {
  const PrivateRoute = <
    // Note: `any` seems to be required for inference - may be related to bivariance
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    TComponent extends React.ComponentType<any>,
    TParamProps extends Exclude<keyof React.ComponentProps<TComponent>, 'children'> = never,
  >({
    component,
    path,
    paramProps: _paramProps,
    ...componentProps
  }: PrivateRouteProps<TComponent, TParamProps>): React.ReactElement => {
    const { error, isAuthenticated, isLoading, loginWithRedirect } = useAuth()
    const location = useLocation()

    const isInvalidState = isInvalidStateError(error)
    const isUserBlocked = isUserBlockedError(error)

    // duplicate logic from `withAuthenticationRequired()` so error can be displayed
    useEffect(() => {
      if (isAuthenticated || isLoading || isUserBlocked) {
        return
      }
      const opts = {
        appState: {
          returnTo: location ? `${location.pathname}${location.search}` : '',
        },
      }
      ;(async (): Promise<void> => {
        await loginWithRedirect(opts)
      })()
    }, [isAuthenticated, isLoading, isUserBlocked, location, loginWithRedirect])

    useLogAndCaptureError(error, () => !isInvalidState && !isUserBlocked)

    if (error && !isInvalidState) {
      return <LoginError error={error} />
    }

    return isAuthenticated ? (
      <Home {...homeProps}>{React.createElement(component, componentProps)}</Home>
    ) : (
      <>
        <Helmet title="Loading" />
        <Container>
          <Spinner loading />
        </Container>
      </>
    )
  }
  return PrivateRoute
}

export const Account: React.FC<AccountProps> = ({ sanityAccountDashboardPage }) => {
  const { sources: rewardsSources, showRewardsOrders } = useRewardsOrders()
  const { legalAgreements } = useSiteMetadata() ?? {}
  const { lang } = useSiteSettings() || {}
  const { userCategory } = useUserCategory()
  const isPatient = userCategory === 'patient'
  const isPractitioner = userCategory === 'practitioner'
  const { getPagePath } = usePageUrl()
  const { data: accountHistoryData, error: accountHistoryError } = useCamelQuery(
    GetCustomerAccountHistoryDocument,
  )
  const showAccountHistory = !!accountHistoryData?.getCustomerAccountHistory

  useLogAndCaptureError(accountHistoryError)

  const showRewards = useMemo(
    () => lang === langCodes.en_US && userCategory === 'practitioner',
    [lang, userCategory],
  )
  const PrivateRoute = useMemo(() => getPrivateRouteComponent({}), [])

  // use dispensary's slug in base path for redirect - dynamic segment is not supported
  const redirectBasePath = getPagePath('/account')

  return (
    <>
      <Head title="My Account" />
      <GlobalStyle includeTypography />
      <Layout showAccountMenu>
        <Router basepath={getRouterBasePath('/account', !!useDispensaryContext())}>
          <PrivateRoute
            path="/"
            component={Dashboard}
            sanityAccountDashboardPage={sanityAccountDashboardPage}
          />
          {/* test and fix redirect */}
          <Redirect from="/dispensary" to={`${redirectBasePath}/dispensary/dashboard`} noThrow />
          {isPractitioner && (
            <PrivateRoute path="/dispensary/dashboard" component={DispensaryDashboard} />
          )}
          {isPractitioner && (
            <PrivateRoute path="/dispensary/promos" component={DispensaryPromosPage} />
          )}
          {isPractitioner && (
            <PrivateRoute path="/dispensary/contact" component={PractitionerDispensaryContact} />
          )}
          {isPatient && (
            <PrivateRoute path="/dispensary/contact" component={PatientDispensaryContact} />
          )}
          {isPractitioner && (
            <PrivateRoute path="/dispensary/customize/logo" component={CustomizeLogoAndBio} />
          )}
          {isPractitioner && (
            <PrivateRoute path="/dispensary/customize/homepage" component={CustomizeHomePage} />
          )}
          {isPractitioner && (
            <PrivateRoute
              path="/dispensary/customize/featured-products"
              component={CustomizeFeaturedProducts}
            />
          )}
          <PrivateRoute path="/addresses" component={AddressBookContainer} />
          <PrivateRoute path="/addresses/new" component={NewAddressContainer} />
          <PrivateRoute path="/addresses/:addressId" component={EditAddressContainer} />
          <Redirect from="/agreements" to={`${redirectBasePath}/settings/agreements`} noThrow />
          <PrivateRoute path="/autoship" component={AutoShipsContainer} />
          <PrivateRoute path="/credit-cards" component={CreditCardsContainer} />
          {/*<PrivateRoute path="/credit-cards/new" component={NewCreditCardContainer} />*/}
          <PrivateRoute path="/orders" component={CustomerOrdersPage} />
          <PrivateRoute path="/reorder" component={ReorderItemsPage} />
          <PrivateRoute
            path="/orders/:orderId"
            component={CustomerOrderPage}
            // `orderId` will be populated by reach-router
            paramProps={['orderId']}
          />
          <PrivateRoute
            path="/orders/:orderId/shipments"
            component={CustomerShipmentsContainer}
            // `orderId` will be populated by reach-router
            paramProps={['orderId']}
          />
          {showRewards && (
            <PrivateRoute path="/rewards/details-payouts" component={RewardsDetailsPayouts} />
          )}
          {showRewardsOrders &&
            rewardsSources.map((rewardsSource) => {
              const { key, slug } = rewardsSource
              return (
                <Fragment key={key}>
                  <PrivateRoute
                    path={`/rewards/orders/${slug}`}
                    component={RewardsOrdersPage}
                    rewardsSource={rewardsSource}
                  />
                  <PrivateRoute
                    path={`/rewards/orders/${slug}/:orderId`}
                    component={RewardsOrderPage}
                    // `orderId` will be populated by reach-router
                    paramProps={['orderId'] as const}
                    rewardsSource={rewardsSource}
                  />
                  <PrivateRoute
                    path={`/rewards/orders/${slug}/:orderId/shipments`}
                    component={RewardsShipmentsContainer}
                    // `orderId` will be populated by reach-router
                    paramProps={['orderId'] as const}
                    rewardsSource={rewardsSource}
                  />
                </Fragment>
              )
            })}
          <Redirect from="/password" to={`${redirectBasePath}/settings/password`} noThrow />
          <PrivateRoute path="/returns" component={Returns} />
          <PrivateRoute path="/settings/password" component={PasswordSettings} />
          {lang === langCodes.en_US && isPractitioner && (
            <>
              <PrivateRoute
                path="/settings/dispensing-options"
                component={DispensingOptionsContainer}
              />
              <PrivateRoute path="/settings/rewards-program" component={RewardsProgramContainer} />
            </>
          )}
          {lang === langCodes.en_US && isPractitioner && (
            // US Practitioner Documents
            <>
              <Redirect
                from="/settings/eft-and-tax"
                to={`${redirectBasePath}/settings/documents`}
                noThrow
              />
              <PrivateRoute path="/settings/documents" component={AccountDocumentsContainer} />
            </>
          )}
          {lang === langCodes.en_CA && isPractitioner && (
            // CA Practitioner Documents
            <>
              <Redirect
                from="/settings/ach-and-w-9"
                to={`${redirectBasePath}/settings/eft-and-tax`}
                noThrow
              />
              <PrivateRoute path="/settings/eft-and-tax" component={EftAndTaxContainer} />
            </>
          )}
          {(isPractitioner || legalAgreements === 'PL') && (
            <PrivateRoute path="/settings/agreements" component={AgreementsContainer} />
          )}
          <PrivateRoute
            path="/transactions"
            component={showAccountHistory ? AccountHistoryContainer : CustomerInvoicesPage}
          />
          <PrivateRoute
            path="/transactions/invoices/:invoiceId"
            component={CustomerInvoiceContainer}
            // `invoiceId` will be populated by reach-router
            paramProps={['invoiceId']}
          />
          <PrivateRoute default component={AccountRouteNotFound} />
        </Router>
      </Layout>
    </>
  )
}
